import {css} from '@emotion/react'
import {useContext, useEffect} from 'react'
import {useDocumentTitle} from '@kensho/tacklebox'

import {H1, H2, H3} from '../components/headings'
import UsageTable from '../components/UsageTable'
import ProductMetadataContext from '../providers/ProductMetadata/Context'
import PricingContext from '../providers/PricingContext'
import LoadingContainer from '../components/LoadingContainer'
import ServicesPageTitle from '../components/ServicesPageTitle'

const sectionCss = css`
  margin-bottom: 60px;
`

const buttonLinkCss = css`
  display: inline-block;
  font-size: 14px;
  padding: 8px 16px;
  text-decoration: none;
  border-radius: 3px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 5e-5) 100%),
    #ebf1f5;
  box-shadow: inset 0px -1px 1px rgba(16, 22, 26, 0.1);
  color: black;

  margin: 10px 0;
`

const loadingContainerCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
`

export default function Services(): JSX.Element {
  useDocumentTitle('Services | Kensho')

  const {state: products, getProductMetadata} = useContext(ProductMetadataContext)
  const prices = useContext(PricingContext)

  useEffect(() => {
    const isCurrent = {value: true}
    if (products.status === 'uninitiated') {
      getProductMetadata(isCurrent)
    }
    return () => {
      isCurrent.value = false
    }
  }, [products.status, getProductMetadata])

  if (
    products.status === 'loading' ||
    products.status === 'uninitiated' ||
    prices === null ||
    prices.status === 'pending'
  ) {
    return <LoadingContainer />
  }

  if (products.status === 'error') {
    return (
      <div css={loadingContainerCss}>
        <H1>Error</H1>
        <H3>{products.error}</H3>
      </div>
    )
  }

  return (
    <>
      <ServicesPageTitle />
      <H1>My Kensho Services</H1>
      {products.productMetadata?.map((productMeta) => (
        <section css={sectionCss} key={productMeta.product.stripeProductId}>
          <H2>{productMeta.product.name}</H2>
          {productMeta.documentationLink && (
            <a
              css={buttonLinkCss}
              href={productMeta.documentationLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              View the API docs
            </a>
          )}
          <UsageTable
            productMeta={productMeta}
            price={
              prices.status === 'ready'
                ? prices.value.find(
                    (price) => price.product === productMeta.product.stripeProductId,
                  )
                : undefined
            }
          />
        </section>
      ))}
    </>
  )
}
